import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronUp, ChevronDown,
  ClipboardList, Boxes, Wrench, Archive, Cloud, Eye, Download,
} from 'lucide-react';
import { auditReportService, AuditReportEntry, AuditReportModule, AuditReportQuery, AuditReportStats } from '../../services/auditReportService';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../hooks/useAuth';
import { exportToCsv } from '../../lib/exportCsv';
import { PageHeader } from '../../components/shared/PageHeader';
import { Pagination } from "../../components/shared/Pagination";
import { StatCard } from '../../components/shared/StatCard';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ListToolbar } from '../../components/shared/ListTabBar';
import { FilterChipsBar, FilterChip } from '../../components/shared/FilterChipsBar';
import { useManagementTableState, ManagementTableState } from '../../hooks/useManagementTableState';
import { AuditAdvancedFilters, AuditFilterState, EMPTY_AUDIT_FILTERS } from './AuditAdvancedFilters';
import CloudAuditPanel from './CloudAuditPanel';
import { useNoWheelHijack } from '../../hooks/useNoWheelHijack';

const MODULE_COLORS: Record<AuditReportModule, string> = {
  Asset: 'bg-primary/10 text-primary',
  License: 'bg-primary/10 text-primary',
  Inventory: 'bg-success/10 text-success',
  Cloud: 'bg-warning/10 text-warning',
};

type TabKey = 'overview' | 'assets' | 'cloudActivity' | 'cloud' | 'licenses' | 'inventory';

/** `module` doubles as the export gate: a tab without one exports nothing from
 * this page's header (Overview is a cross-module summary, and Cloud Audit is a
 * different report with its own export button inside CloudAuditPanel).
 * `exportFile` names the download after the tab so an Asset Audit export is
 * not indistinguishable from a License one on disk. */
const TABS: { key: TabKey; label: string; module?: AuditReportModule; exportFile?: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'assets', label: 'Asset Audit', module: 'Asset', exportFile: 'asset-audit' },
  // Create/update/delete on cloud resources, read from CloudResourceHistory —
  // same shape as the Asset/License/Inventory feeds beside it.
  { key: 'cloudActivity', label: 'Cloud Activity', module: 'Cloud', exportFile: 'cloud-activity-audit' },
  // No `module`: this one is a data-completeness report over the cloud
  // register (Story 88), not a slice of the shared activity log — it re-derives
  // findings from the current register on every read rather than reading events.
  { key: 'cloud', label: 'Cloud Audit' },
  { key: 'licenses', label: 'License Audit', module: 'License', exportFile: 'license-audit' },
  { key: 'inventory', label: 'Inventory Audit', module: 'Inventory', exportFile: 'inventory-audit' },
];

/** Tab switches carry the filters that only make sense within one module across —
 * dropping Action/Entity Type (module-specific) while keeping search and the
 * date range (a reporting window the reviewer is usually holding constant). */
function withoutModuleFacets(filters: AuditFilterState): AuditFilterState {
  return { ...filters, action: undefined, entityType: undefined };
}

function entityCell(e: AuditReportEntry): { name: string; code: string } {
  const type = e.entityType.replace(/_/g, ' ');
  return {
    name: e.entityName || (type.charAt(0).toUpperCase() + type.slice(1)),
    code: e.entityCode || (e.entityId ? `#${e.entityId}` : '—'),
  };
}

const AuditReportPage: React.FC = () => {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const tabsRef = useNoWheelHijack<HTMLDivElement>();

  const [entries, setEntries] = useState<AuditReportEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<AuditReportStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [viewEntry, setViewEntry] = useState<AuditReportEntry | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  const loadEntries = useCallback(
    async (state: ManagementTableState<AuditFilterState>, signal: AbortSignal) => {
      // The Cloud Audit tab renders its own panel from a different endpoint;
      // fetching the activity log here would be a wasted call and, on a
      // cloud-only role, a spurious "failed to load" toast.
      if (state.tab === 'cloud') return;
      setLoading(true);
      try {
        const module = TABS.find((t) => t.key === state.tab)?.module;
        const query: AuditReportQuery = { page: state.page, limit: state.pageSize };
        if (module) query.module = module;
        if (state.filters.action) query.action = state.filters.action;
        if (state.search) query.search = state.search;
        if (state.filters.startDate) query.startDate = state.filters.startDate;
        if (state.filters.endDate) query.endDate = state.filters.endDate;

        const result = await auditReportService.getAll(query, signal);
        let data = result.data;
        if (state.filters.entityType) {
          data = data.filter((e) => e.entityType === state.filters.entityType);
        }
        if (state.sortDir === 'asc') data = [...data].reverse();
        setEntries(data);
        setTotal(result.total);
      } catch (error: any) {
        // Superseded by a newer request, or the component unmounted — not a failure.
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
        showToast('Failed to load audit report', 'error');
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    },
    [showToast],
  );

  const qts = useManagementTableState<AuditFilterState>({
    emptyFilters: EMPTY_AUDIT_FILTERS,
    defaultTab: 'overview',
    defaultPageSize: 10,
    defaultSortBy: 'timestamp',
    defaultSortDir: 'desc',
    onStateChange: loadEntries,
  });

  const activeTab = qts.state.tab as TabKey;
  const activeModule = TABS.find((t) => t.key === activeTab)?.module;

  const availableActions = useMemo(() => {
    const set = new Set(entries.map(e => e.action));
    return Array.from(set).sort();
  }, [entries]);

  const availableEntityTypes = useMemo(() => {
    const set = new Set(entries.map(e => e.entityType));
    return Array.from(set).sort();
  }, [entries]);

  const changeTab = (key: TabKey) => {
    qts.setTab(key);
    qts.setFilters(withoutModuleFacets(qts.state.filters));
  };

  // Filter-aware CSV export (User Story 114) — mirrors loadEntries' query
  // building, minus pagination, so the file matches exactly what's on screen.
  // Scoped to one module per tab: the button is only rendered on a tab that has
  // one, and the module is always sent, so a tab can never export another
  // tab's rows alongside its own.
  const handleExportCsv = async () => {
    const state = qts.state;
    const tab = TABS.find((t) => t.key === state.tab);
    if (!tab?.module) return;
    setExporting(true);
    try {
      const query: AuditReportQuery = { module: tab.module };
      if (state.filters.action) query.action = state.filters.action;
      if (state.search) query.search = state.search;
      if (state.filters.startDate) query.startDate = state.filters.startDate;
      if (state.filters.endDate) query.endDate = state.filters.endDate;

      let data = await auditReportService.exportAll(query);
      if (state.filters.entityType) {
        data = data.filter((e) => e.entityType === state.filters.entityType);
      }
      if (data.length === 0) {
        showToast('No data to export', 'warning');
        return;
      }

      const headers = [
        'Date & Time', 'Module', 'Entity Name', 'Entity Code', 'Action',
        'Received/Returned By', 'Received/Returned By Email',
        'Performed By',
        ...(tab.key === 'assets' ? [] : ['Performed By Email']),
        'Reason', 'Details',
      ];
      const rows = data.map((e) => {
        const entity = entityCell(e);
        return [
          new Date(e.timestamp).toLocaleString(),
          e.module,
          entity.name,
          entity.code,
          e.action.replace(/_/g, ' '),
          e.personName || '',
          e.personEmail || '',
          e.actorName,
          ...(tab.key === 'assets' ? [] : [e.actorEmail || '']),
          e.reason || '',
          e.details || '',
        ];
      });
      exportToCsv(`${tab.exportFile}_${new Date().toISOString().split('T')[0]}.csv`, headers, rows);
      showToast(`${tab.label} exported to CSV successfully`, 'success');
    } catch {
      showToast('Failed to export audit report', 'error');
    } finally {
      setExporting(false);
    }
  };

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const query: AuditReportQuery = {};
      if (qts.state.filters.startDate) query.startDate = qts.state.filters.startDate;
      if (qts.state.filters.endDate) query.endDate = qts.state.filters.endDate;
      const result = await auditReportService.getStats(query);
      setStats(result);
    } catch {
      // non-critical — leave stats blank
    } finally {
      setStatsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qts.state.filters.startDate, qts.state.filters.endDate]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const filterChips: FilterChip[] = useMemo(() => {
    const applied = qts.state.filters;
    const chips: FilterChip[] = [];
    if (applied.action) {
      chips.push({
        key: 'action',
        label: `Action: ${applied.action.replace(/_/g, ' ')}`,
        onRemove: () => qts.setFilters({ ...applied, action: undefined }),
      });
    }
    if (applied.entityType) {
      chips.push({
        key: 'entityType',
        label: `Entity: ${applied.entityType.replace(/_/g, ' ')}`,
        onRemove: () => qts.setFilters({ ...applied, entityType: undefined }),
      });
    }
    if (applied.startDate) {
      chips.push({
        key: 'startDate',
        label: `From: ${applied.startDate}`,
        onRemove: () => qts.setFilters({ ...applied, startDate: undefined }),
      });
    }
    if (applied.endDate) {
      chips.push({
        key: 'endDate',
        label: `To: ${applied.endDate}`,
        onRemove: () => qts.setFilters({ ...applied, endDate: undefined }),
      });
    }
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qts.state.filters]);

  const totalPages = Math.max(1, Math.ceil(total / qts.state.pageSize));

  return (
    <div className="space-y-6">
      <PageHeader title="Audit" description="Track and review all system activities across Assets, Licenses, Inventory and Cloud">
        {activeModule && hasPermission('reports.export') && (
          <Button variant="outline" size="sm" onClick={handleExportCsv} disabled={exporting || loading}>
            <Download className="mr-2 h-4 w-4" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        )}
      </PageHeader>

      {/* Tabs */}
      <div ref={tabsRef} className="border-b px-1 overflow-x-auto overflow-y-hidden no-scrollbar">
        <Tabs value={activeTab} onValueChange={(v) => changeTab(v as TabKey)}>
          <TabsList className="bg-transparent h-auto p-0 gap-8 border-none flex justify-start">
            {TABS.map(t => (
              <TabsTrigger
                key={t.key}
                value={t.key}
                className="group relative data-[state=active]:text-primary data-[state=active]:shadow-none rounded-none px-0 pb-3 pt-4 bg-transparent whitespace-nowrap text-muted-foreground hover:text-foreground transition-all border-none"
              >
                <span className="font-semibold text-sm tracking-tight">{t.label}</span>
                {activeTab === t.key && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary animate-in fade-in slide-in-from-bottom-1" />
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {activeTab === 'cloud' ? (
        <CloudAuditPanel />
      ) : (
      <>

      {/* Stat cards */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
        <StatCard
          title="Total Activities"
          value={stats?.total ?? 0}
          subtitle="All modules"
          icon={ClipboardList}
          iconColor="bg-primary/10 text-primary"
          loading={statsLoading}
        />
        <StatCard
          title="Asset Activities"
          value={stats?.asset ?? 0}
          subtitle="Deployments & returns"
          icon={Boxes}
          iconColor="bg-success/10 text-success"
          loading={statsLoading}
        />
        <StatCard
          title="License Activities"
          value={stats?.license ?? 0}
          subtitle="Assignments & renewals"
          icon={Wrench}
          iconColor="bg-primary/10 text-primary"
          loading={statsLoading}
        />
        <StatCard
          title="Inventory Activities"
          value={stats?.inventory ?? 0}
          subtitle="Stock movements"
          icon={Archive}
          iconColor="bg-warning/10 text-warning"
          loading={statsLoading}
        />
        <StatCard
          title="Cloud Activities"
          value={stats?.cloud ?? 0}
          subtitle="Creates & updates"
          icon={Cloud}
          iconColor="bg-warning/10 text-warning"
          loading={statsLoading}
        />
      </div>

      {/* Search + Filters, matching the Assets/Licenses/Cloud list-page pattern:
          free-text search stays on the toolbar, everything else lives behind
          the Filters drawer with removable chips underneath. */}
      <Card>
        <ListToolbar
          searchValue={qts.searchInput}
          onSearchChange={qts.setSearchInput}
          searchPlaceholder="Search by entity name, ID, or reason..."
          activeFilterCount={qts.activeFilterCount}
          onOpenFilters={() => { qts.syncDraft(); setShowFilters(true); }}
          onClearAll={qts.clearSearchAndFilters}
          hasActive={qts.hasActiveSearchOrFilters}
        />
        <FilterChipsBar chips={filterChips} onClearAll={qts.clearFilters} />

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40 text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium whitespace-nowrap">
                    <button className="flex items-center gap-1 hover:text-foreground" onClick={() => qts.toggleSort('timestamp')}>
                      Date &amp; Time
                      {qts.state.sortDir === 'desc' ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Module</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">
                    {activeModule === 'License' ? 'Plan & ID' : 'Entity (Name & ID)'}
                  </th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Action</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Received/Returned By<br/>(Name &amp; Email)</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Performed By<br/>(Name &amp; Email)</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Reason</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap">Details</th>
                  <th className="px-4 py-3 font-medium whitespace-nowrap text-right">View</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr><td colSpan={9} className="text-center py-12 text-muted-foreground">Loading...</td></tr>
                ) : entries.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-12 text-muted-foreground">No activity found</td></tr>
                ) : entries.map((e) => {
                  const entity = entityCell(e);
                  return (
                    <tr key={e.id} className="hover:bg-muted/30 align-top">
                      <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">{new Date(e.timestamp).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${MODULE_COLORS[e.module]}`}>{e.module}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-primary">{entity.name}</div>
                        <div className="text-xs text-muted-foreground">{entity.code}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap capitalize font-medium">{e.action.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {e.personName ? (
                          <>
                            <div>{e.personName}</div>
                            {e.personEmail && <div className="text-xs text-muted-foreground">{e.personEmail}</div>}
                          </>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div>{e.actorName}</div>
                        {e.actorEmail && <div className="text-xs text-muted-foreground">{e.actorEmail}</div>}
                      </td>
                      <td className="px-4 py-3 max-w-[180px] truncate" title={e.reason}>{e.reason || '—'}</td>
                      <td className="px-4 py-3 max-w-[200px] truncate" title={e.details}>{e.details || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="icon-sm" onClick={() => setViewEntry(e)} title="View Details">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={qts.state.page}
            totalPages={totalPages}
            onPageChange={qts.setPage}
            totalItems={total}
            pageSize={qts.state.pageSize}
            onPageSizeChange={qts.setPageSize}
          />
        </CardContent>
      </Card>

      <AuditAdvancedFilters
        open={showFilters}
        onOpenChange={setShowFilters}
        draft={qts.draftFilters}
        onDraftChange={qts.setDraftFilters}
        onApply={qts.applyDraft}
        onClear={qts.clearFilters}
        actionOptions={availableActions}
        entityTypeOptions={availableEntityTypes}
      />

      {/* Entry detail modal */}
      <Dialog open={!!viewEntry} onOpenChange={(open) => !open && setViewEntry(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Activity Details</DialogTitle></DialogHeader>
          {viewEntry && (
            <div className="space-y-3 pt-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Date &amp; Time</span><span>{new Date(viewEntry.timestamp).toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Module</span><span className={`px-2 py-0.5 rounded text-xs font-medium ${MODULE_COLORS[viewEntry.module]}`}>{viewEntry.module}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Entity</span><span>{entityCell(viewEntry).name} ({entityCell(viewEntry).code})</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Action</span><span className="capitalize font-medium">{viewEntry.action.replace(/_/g, ' ')}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Received/Returned By</span><span>{viewEntry.personName ? `${viewEntry.personName}${viewEntry.personEmail ? ' (' + viewEntry.personEmail + ')' : ''}` : '—'}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Performed By</span><span>{viewEntry.actorName}{viewEntry.actorEmail ? ` (${viewEntry.actorEmail})` : ''}</span></div>
              <div className="pt-2 border-t">
                <div className="text-muted-foreground mb-1">Reason</div>
                <div>{viewEntry.reason || '—'}</div>
              </div>
              <div className="pt-2 border-t">
                <div className="text-muted-foreground mb-1">Details</div>
                <div>{viewEntry.details || '—'}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      </>
      )}
    </div>
  );
};

export default AuditReportPage;
